import type {
  ClientConfig,
  RadarConfig,
} from "@/features/client-config/types/client-config";
import { radarConsentService } from "@/features/radar/services/radar-consent/radar-consent";
import { radarSnapshotService } from "@/features/radar/services/radar-snapshot/radar-snapshot";
import { isShotFromSelectedLaunchArea } from "@/features/radar/utils/is-shot-from-selected-launch-area";
import { onCourseTracerStore } from "@/features/trajectory/services/on-course-tracer-store";
import { sessionTracking } from "@/services/analytics/session-tracking";
import {
  WebSocketClient,
  type WebSocketConnectionState,
  type WebSocketEvent,
  type WebSocketMessage,
} from "@/services/connectivity/websocket-client/websocket-client";

import type {
  Shot,
  LaunchMeasurementMessage,
  FinalMeasurementMessage,
} from "../types/shot.types";
import {
  buildFlyingShot,
  buildLandedShotFromScratch,
  buildLandedShotFromUpdate,
} from "../utils/shot-builder";

export type GlobalWebSocketEvent = {
  type: "shot-detected" | "connected" | "disconnected";
  shot?: Shot;
  message?: WebSocketMessage;
};

type ContextMethods = {
  addShot: (shot: Shot) => void;
  updateShot: (shot: Shot) => void;
  getShots: () => Shot[];
  consented: () => boolean;
  setConsented: (consented: boolean) => void;
};

/**
 * Normalizes the new envelope format `{ type: "/measurement/...", msg }` into
 * the existing uppercase-keyed shape so downstream routing stays unchanged.
 * Non-matching messages are returned as-is.
 */
export function normalizeMessage(raw: unknown): unknown {
  const m = raw as { type?: string; msg?: unknown } | null;
  if (!m || typeof m.type !== "string" || !m.msg) {
    return raw;
  }

  if (m.type === "/measurement/launch") {
    return { Type: "Measurement", SubType: "LaunchData", Payload: m.msg };
  }

  if (m.type === "/measurement/final") {
    return { Type: "Measurement", SubType: "Measurement", Payload: m.msg };
  }

  return raw;
}

type RadarConnection = {
  client: WebSocketClient;
  unsubscribe: () => void;
};

/**
 * Global WebSocket Manager
 *
 * Handles WebSocket connections and shot detection across the entire app.
 * Holds one WebSocketClient per radar so each radar connects, retries, and
 * disconnects independently. Directly updates React context - no
 * intermediate managers or storage layers.
 */
type RawListener = (radarId: string, event: WebSocketEvent) => void;

class GlobalWebSocketManager {
  private listeners: ((event: GlobalWebSocketEvent) => void)[] = [];
  private rawListeners: RawListener[] = [];
  private isInitialized = false;
  private isInitializing = false;
  private connections = new Map<string, RadarConnection>();
  private contextMethods: ContextMethods | null = null;
  private pendingInitialize: RadarConfig[] | null = null;
  private cachedConfig: ClientConfig | null = null;

  /**
   * Register context methods so WebSocket can update React state directly
   */
  registerContext(methods: ContextMethods | null): void {
    this.contextMethods = methods;
  }

  /**
   * Push the current client config in so the hot shot-ingestion path
   * (routeMeasurement, consent lookups) reads an in-memory copy instead of
   * hitting storage on every websocket frame. Call whenever the config
   * changes (e.g. from the client-config context).
   */
  setClientConfig(config: ClientConfig | null): void {
    this.cachedConfig = config;
  }

  /**
   * Create/tear down one WebSocketClient per radar, diffing against the
   * previous set of radars so unaffected connections are left alone.
   *
   * Returns per-radar connect success so callers can detect partial/total
   * failure. If called again while already running, this call's radar list
   * is recorded as the latest pending request and `undefined` is returned
   * immediately; the in-flight run picks it up as soon as it finishes and
   * runs it next (rather than the second request being silently dropped).
   */
  async initialize(
    radars: RadarConfig[]
  ): Promise<Map<string, boolean> | undefined> {
    if (this.isInitializing) {
      this.pendingInitialize = radars;
      return undefined;
    }
    this.isInitializing = true;

    try {
      const nextIds = new Set(radars.map((radar) => radar.id));

      for (const [radarId, connection] of this.connections) {
        if (!nextIds.has(radarId)) {
          connection.unsubscribe();
          connection.client.disconnect();
          this.connections.delete(radarId);
        }
      }

      const connectResults = await Promise.all(
        radars.map(
          async (radar) => [radar.id, await this.connectRadar(radar)] as const
        )
      );

      this.isInitialized = true;

      return new Map(connectResults);
    } finally {
      this.isInitializing = false;

      const pending = this.pendingInitialize;
      if (pending) {
        this.pendingInitialize = null;
        void this.initialize(pending);
      }
    }
  }

  private async connectRadar(radar: RadarConfig): Promise<boolean> {
    const existing = this.connections.get(radar.id);
    if (existing) {
      existing.client.setUrl(radar.webSocketBaseUrl);
      try {
        await existing.client.connect();
        return true;
      } catch (error) {
        console.error(`WebSocket reconnect failed [${radar.id}]:`, error);
        return false;
      }
    }

    const client = new WebSocketClient(radar.id, radar.webSocketBaseUrl);
    const unsubscribe = client.addListener((event) => {
      this.handleWebSocketEvent(radar.id, event);
    });
    this.connections.set(radar.id, { client, unsubscribe });

    try {
      await client.connect();
      return true;
    } catch (error) {
      console.error(`WebSocket initialization failed [${radar.id}]:`, error);
      return false;
    }
  }

  /**
   * Add a listener for global WebSocket events
   */
  addListener(listener: (event: GlobalWebSocketEvent) => void): () => void {
    this.listeners.push(listener);

    // Immediately notify the listener of the current aggregate connection state
    if (this.isConnected()) {
      listener({ type: "connected" });
    } else {
      listener({ type: "disconnected" });
    }

    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Add a listener for raw per-radar WebSocket events (connection lifecycle
   * and every message frame, before shot-detection routing). Used by
   * debug/monitoring surfaces that need per-radar visibility.
   */
  addRawListener(listener: RawListener): () => void {
    this.rawListeners.push(listener);
    return () => {
      const index = this.rawListeners.indexOf(listener);
      if (index > -1) {
        this.rawListeners.splice(index, 1);
      }
    };
  }

  /**
   * Handle WebSocket events for a specific radar
   */
  private handleWebSocketEvent(radarId: string, event: WebSocketEvent): void {
    this.rawListeners.forEach((listener) => {
      try {
        listener(radarId, event);
      } catch (error) {
        console.error("WebSocket raw listener error:", error);
      }
    });

    // Track connection status (aggregated across all radars)
    if (event.type === "connected" || event.type === "disconnected") {
      this.notifyListeners({
        type: this.isConnected() ? "connected" : "disconnected",
      });
      return;
    }

    // Handle shot messages
    if (event.type === "message" && event.message) {
      const rawMessage = normalizeMessage(event.message) as {
        Id?: string;
        Type?: string;
        SubType?: string;
        Payload?: unknown;
        type?: string;
      };

      // Skip client-originated messages (lowercase type)
      if (rawMessage.type && !rawMessage.Type) {
        return;
      }

      // Check if this is a server measurement message (uppercase Type)
      if (rawMessage.Type === "Measurement") {
        void this.routeMeasurement(radarId, rawMessage);
      }
    }
  }

  private async routeMeasurement(
    radarId: string,
    rawMessage: {
      SubType?: string;
      Payload?: unknown;
    }
  ): Promise<void> {
    try {
      const radar = this.cachedConfig?.radars?.find(
        (entry) => entry.id === radarId
      );
      const payload = rawMessage.Payload as { BayName?: string } | undefined;

      if (
        !isShotFromSelectedLaunchArea({
          bayName: payload?.BayName,
          launchAreas:
            radar?.systemConfig?.Tracking.Geometry.Environment.LaunchAreas,
          selectedLaunchAreaIds: radar?.selectedLaunchAreaIds,
        })
      ) {
        return;
      }

      if (rawMessage.SubType === "LaunchData") {
        await this.handleShotFlying(
          radarId,
          rawMessage as LaunchMeasurementMessage
        );
      } else if (rawMessage.SubType === "Measurement") {
        await this.handleShotLanded(
          radarId,
          rawMessage as FinalMeasurementMessage
        );
      }
    } catch (error) {
      console.error(`Failed to route measurement [${radarId}]:`, error);
    }
  }

  /**
   * Handle shot-flying event (Launch measurement)
   */
  private async handleShotFlying(
    radarId: string,
    message: LaunchMeasurementMessage
  ): Promise<void> {
    const payload = message.Payload;
    if (!payload || !this.contextMethods) {
      return;
    }

    try {
      const consentEnabled = this.cachedConfig?.consentEnabled ?? true;
      const hasConsent = !consentEnabled || this.contextMethods.consented();

      const shot = buildFlyingShot(payload, hasConsent, radarId);

      this.contextMethods.addShot(shot);
      if (consentEnabled) {
        this.contextMethods.setConsented(false);
      }
      sessionTracking.track("shot_launched", {
        consented: hasConsent,
        shotId: shot.id,
      });
      this.notifyListeners({ type: "shot-detected", shot });
    } catch (error) {
      console.error(`Failed to handle shot flying [${radarId}]:`, error);
    }
  }

  /**
   * Handle shot-landed event (Final measurement)
   */
  private async handleShotLanded(
    radarId: string,
    message: FinalMeasurementMessage
  ): Promise<void> {
    const payload = message.Payload;
    if (!payload || !this.contextMethods) {
      return;
    }

    try {
      const existingShot = this.contextMethods
        .getShots()
        .find((s) => s.id === payload.Id);

      if (existingShot) {
        const updatedShot = buildLandedShotFromUpdate(existingShot, payload);

        this.contextMethods.updateShot(updatedShot);
        this.notifyListeners({ type: "shot-detected", shot: updatedShot });

        const consentEnabled = this.cachedConfig?.consentEnabled ?? true;
        const hasConsent = !consentEnabled || (updatedShot.hasConsent ?? false);

        sessionTracking.track("shot_landed", {
          consented: hasConsent,
          shotId: updatedShot.id,
        });

        radarConsentService
          .reportConsent(
            updatedShot.radarId,
            updatedShot.id,
            hasConsent,
            consentEnabled
          )
          .catch(() => {
            // Errors are already logged and sent to Sentry in the service
          });

        if (hasConsent) {
          // Use launch timestamp as the If-Modified-Since anchor so we accept
          // frames written after the ball left the club, not after measurement completed.
          radarSnapshotService
            .captureForShot(
              existingShot.radarId,
              existingShot.id,
              existingShot.timestamp
            )
            .catch(() => {
              // Errors are already logged and sent to Sentry in the service
            });
        }

        if (payload.oncourse_tracer) {
          onCourseTracerStore.set(payload.Id, payload.oncourse_tracer);
        }
      } else {
        // Missed the initial flying event — reconstruct from landing data
        const consentEnabled = this.cachedConfig?.consentEnabled ?? true;
        const hasConsent = !consentEnabled || this.contextMethods.consented();

        const shot = buildLandedShotFromScratch(payload, hasConsent, radarId);

        this.contextMethods.addShot(shot);
        sessionTracking.track("shot_landed", {
          consented: hasConsent,
          shotId: shot.id,
        });
        this.notifyListeners({ type: "shot-detected", shot });

        radarConsentService
          .reportConsent(shot.radarId, shot.id, hasConsent, consentEnabled)
          .catch(() => {
            // Errors are already logged and sent to Sentry in the service
          });

        if (shot.hasConsent) {
          radarSnapshotService
            .captureForShot(shot.radarId, shot.id, shot.timestamp)
            .catch(() => {
              // Errors are already logged and sent to Sentry in the service
            });
        }

        if (payload.oncourse_tracer) {
          onCourseTracerStore.set(payload.Id, payload.oncourse_tracer);
        }
      }
    } catch (error) {
      console.error(`Failed to handle shot landed [${radarId}]:`, error);
    }
  }

  private notifyListeners(event: GlobalWebSocketEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error("WebSocket listener error:", error);
      }
    });
  }

  /**
   * Check if the manager is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get connection status — true if at least one radar is connected
   */
  isConnected(): boolean {
    return Array.from(this.connections.values()).some((connection) =>
      connection.client.isConnected()
    );
  }

  /**
   * Get connection status for a specific radar
   */
  isRadarConnected(radarId: string): boolean {
    return this.connections.get(radarId)?.client.isConnected() ?? false;
  }

  /**
   * Get connection state for every radar, keyed by radar id
   */
  getAllStatuses(): Record<string, WebSocketConnectionState> {
    const statuses: Record<string, WebSocketConnectionState> = {};
    for (const [radarId, connection] of this.connections) {
      statuses[radarId] = connection.client.getConnectionState();
    }
    return statuses;
  }

  /**
   * Refresh client ID and reconnect if needed, for every radar connection
   */
  async refreshClientId(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }
    await Promise.all(
      Array.from(this.connections.values()).map((connection) =>
        connection.client.refreshClientId()
      )
    );
  }

  /**
   * Force reconnection for one radar, or all radars if none is given
   * (useful after app updates)
   */
  async forceReconnect(radarId?: string): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    const targets = radarId
      ? [this.connections.get(radarId)].filter(
          (connection): connection is RadarConnection => !!connection
        )
      : Array.from(this.connections.values());

    for (const connection of targets) {
      connection.client.disconnect();
    }
    await Promise.all(targets.map((connection) => connection.client.connect()));
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    for (const connection of this.connections.values()) {
      connection.unsubscribe();
      connection.client.disconnect();
    }
    this.connections.clear();
    this.listeners = [];
    this.rawListeners = [];
    this.contextMethods = null;
    this.isInitialized = false;
    this.isInitializing = false;
  }
}

// Export singleton instance
export const globalWebSocketManager = new GlobalWebSocketManager();
